/*
 * (c) Copyright, 2026, Medidata Solutions Worldwide, Inc. All rights reserved.
 *
 * EOS Overview Workflows Table
 *
 * Migrated from: lego/page/eos/overview/StatusDocuments/EosOverviewWorkflowsTable/EosOverviewWorkflowsTable.jsx
 *
 * 7-column table: Site, Country, Date Initiated, WF#, Phase, Status (progress bar), Time to Complete
 *
 * Reuses:
 *   - WorkflowDetailsModal (~/components/modals/WorkflowDetailsModal)
 *   - DateCell (~/components/shared/DateCell)
 *   - WorkflowStatus constants (~/constants/workflow)
 */

import { useTranslation } from 'react-i18next'
import { useModal } from '../../../../contexts/ModalContext'
import WorkflowDetailsModal from '../../../../components/modals/WorkflowDetailsModal'
import DateCell from '../../../../components/shared/DateCell'
import { WorkflowStatus } from '../../../../constants/workflow'
import type { EosWorkflowsTableProps } from '../types'

const EosOverviewWorkflowsTable: React.FC<EosWorkflowsTableProps> = ({ filesList, wasFiltered }) => {
  const { t } = useTranslation()
  const { showModal } = useModal()

  return (
    <table className="styled-table chita-table mb0 EosOverviewWorkflowsTable">
      <thead>
        <tr>
          <th>{t('EMTF_MD_SITE')}</th>
          <th>{t('EMTF_MD_COUNTRY')}</th>
          <th>{t('TABLE_TH_DATE_INITIATED')}</th>
          <th>{t('REPORTS_TABLE_TH_WF_NO')}</th>
          <th>{t('CURRENT_PHASE')}</th>
          <th>{t('STATUS')}</th>
          <th>{t('REPORTS_TABLE_TH_WF_TIME_TO_COMPLETE')}</th>
        </tr>
      </thead>

      {(!filesList || filesList.length === 0) && (
        <tbody>
          <tr className="no-results">
            <td colSpan={9}>{t('NO_RESULTS_FILTERED')}</td>
          </tr>
        </tbody>
      )}

      <tbody>
        {filesList?.map((file, index) => (
          <tr key={index}>
            <td>{file.siteFolderName ? file.siteFolderName : <span>{t('NOT_AVAILABLE')}</span>}</td>
            <td>{file.country ? file.country : <span>{t('NOT_AVAILABLE')}</span>}</td>
            <td>{file.wfInitiatedAt ? <DateCell val={file.wfInitiatedAt} /> : <span>{t('NOT_AVAILABLE')}</span>}</td>
            <td>
              {file.formattedWfId ? (
                <span className="text-link" onClick={() => showModal(<WorkflowDetailsModal wfId={file.wfId!} />)}>
                  {file.formattedWfId}
                </span>
              ) : (
                <span>{t('NOT_AVAILABLE')}</span>
              )}
            </td>
            <td>{file.activeWorkflowStep ? file.activeWorkflowStep : <span>{t('NOT_AVAILABLE')}</span>}</td>
            <td className="status">
              {!!file.wfId && (
                <div
                  onClick={() => showModal(<WorkflowDetailsModal wfId={file.wfId!} />)}
                  className={
                    'status-wrapper' +
                    (file.workflowStatus === WorkflowStatus.CANCELLED ||
                    file.workflowStatus === WorkflowStatus.SUSPENDED ||
                    file.rejected
                      ? ' canceled'
                      : '')
                  }
                >
                  <span style={{ width: `${file.progress ?? 0}%` }} className={file.progress === 100 ? 'w100p' : ''} />
                </div>
              )}
            </td>
            <td>{file.timeToComplete ? file.timeToComplete : <span>{t('NOT_AVAILABLE')}</span>}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

export default EosOverviewWorkflowsTable
